/**
 * features/suppliers/SupplierFormDialog.tsx
 */

import { useEffect, useState } from 'react';
import { supplierInputSchema, type SupplierInput } from '@ft/core';
import { ApiError } from '~/lib/api';
import type { Party } from '~/lib/types';
import { Dialog, useToast } from '~/ui/overlays';
import { Button, Field, Input, Textarea } from '~/ui/primitives';
import { TaxIdLookupField } from '~/features/tax-lookup/TaxIdLookupField';
import { useCreateSupplier, useUpdateSupplier } from './hooks';

type FormState = SupplierInput;

const EMPTY: FormState = {
  name: '',
  taxId: '',
  branch: '',
  address: '',
  phone: '',
  email: '',
  notes: '',
};

export function SupplierFormDialog({
  open,
  onClose,
  supplier,
}: {
  open: boolean;
  onClose: () => void;
  supplier: Party | null;
}) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const create = useCreateSupplier();
  const update = useUpdateSupplier();
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(
      supplier
        ? {
            name: supplier.name,
            taxId: supplier.taxId,
            branch: supplier.branch,
            address: supplier.address,
            phone: supplier.phone,
            email: supplier.email,
            notes: supplier.notes,
          }
        : EMPTY,
    );
  }, [open, supplier]);

  const saving = create.isPending || update.isPending;

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit() {
    const parsed = supplierInputSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof FormState, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormState;
        fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    try {
      if (supplier) {
        await update.mutateAsync({ id: supplier.id, input: parsed.data });
        toast.success('บันทึกข้อมูลผู้ขายแล้ว');
      } else {
        await create.mutateAsync(parsed.data);
        toast.success('เพิ่มผู้ขายใหม่แล้ว');
      }
      onClose();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={supplier ? 'แก้ไขข้อมูลผู้ขาย' : 'เพิ่มผู้ขายใหม่'}
      footer={
        <>
          <Button onClick={onClose} disabled={saving}>
            ยกเลิก
          </Button>
          <Button variant="primary" onClick={() => void handleSubmit()} loading={saving}>
            บันทึก
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="ชื่อผู้ขาย/คู่ค้า" required error={errors.name}>
          <Input
            value={form.name}
            onChange={(event) => setField('name', event.target.value)}
            invalid={Boolean(errors.name)}
            autoFocus
          />
        </Field>

        <TaxIdLookupField
          value={form.taxId}
          onChange={(value) => setField('taxId', value)}
          error={errors.taxId}
          onFound={(branch) => {
            setForm((current) => ({
              ...current,
              taxId: branch.taxId,
              branch: branch.branch,
              name: current.name || branch.name,
              address: branch.address || current.address,
              phone: branch.phone || current.phone,
            }));
          }}
        />

        <Field label="ที่อยู่" error={errors.address}>
          <Textarea
            value={form.address}
            onChange={(event) => setField('address', event.target.value)}
            invalid={Boolean(errors.address)}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="โทรศัพท์" error={errors.phone}>
            <Input value={form.phone} onChange={(event) => setField('phone', event.target.value)} />
          </Field>
          <Field label="อีเมล" error={errors.email}>
            <Input
              type="email"
              value={form.email}
              onChange={(event) => setField('email', event.target.value)}
            />
          </Field>
        </div>

        <Field label="หมายเหตุ" error={errors.notes}>
          <Textarea value={form.notes} onChange={(event) => setField('notes', event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
